var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BoxGeometry, BufferAttribute, BufferGeometry, ConeGeometry, CylinderGeometry, Matrix4, PlaneGeometry, Quaternion, SphereGeometry, Vector3 } from "three";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
import { CollisionMeshShapeType, isCollisionMeshShape, isCompoundShape, PrimitiveShape } from "flavr-common";
import { RequestMeshGeometryMessage, ResponseMeshGeometryMessage } from "../mesh/MeshMessages";
/**
 * Creates collision geometry of either a primitive, mesh or compound shape depending on the value of `shape`
 */
export const createColliderGeometry = (shape, connection) => __awaiter(void 0, void 0, void 0, function* () {
    let geometry;
    if (isCompoundShape(shape)) {
        geometry = yield createColliderCompoundGeometry(shape, connection);
    }
    else if (isCollisionMeshShape(shape)) {
        geometry = yield createColliderNonPrimitiveGeometry(shape, connection);
    }
    else {
        geometry = createColliderPrimitiveGeometry(shape);
    }
    return geometry;
});
/**
 * Creates a geometry primitive like Box, Sphere, Cylinder, Cone, Plane, Pyramid or Prism.
 *
 * **Note that Prism is not yet implemented.**
 */
export const createColliderPrimitiveGeometry = (shape) => {
    switch (shape) {
        case PrimitiveShape.Box:
            return new BoxGeometry();
        case PrimitiveShape.Sphere:
            return new SphereGeometry(0.5); // Customized to match blenders sizing
        case PrimitiveShape.Cylinder:
            return new CylinderGeometry(0.5, 0.5); // Customized to match blenders sizing
        case PrimitiveShape.Cone:
            return new ConeGeometry(0.5); // Customized to match blenders sizing
        case PrimitiveShape.Plane:
            return new PlaneGeometry();
        case PrimitiveShape.Pyramid:
            return new CylinderGeometry(0, 1, 1, 4); // pyramid with 4 sides customized to match blenders sizing
        case PrimitiveShape.Prism:
            throw new Error('[selection] Prism shape not yet supported');
    }
};
/**
 * Creates either Mesh, Hull, Heightfield, HACD or VHACD geometry depending on the value of `collisionMeshShape.shape`.
 *
 * **Note that HACD and VHACS are currently not implemented**
 */
export const createColliderNonPrimitiveGeometry = (collisionMeshShape, connection) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield connection.askByMessageType(new RequestMeshGeometryMessage(collisionMeshShape.meshUrl, collisionMeshShape.pieceName), ResponseMeshGeometryMessage);
    switch (collisionMeshShape.shape) {
        case CollisionMeshShapeType.MESH:
            return createColliderMeshGeometry(response.vertices, response.indexes, response.matrices);
        case CollisionMeshShapeType.HULL:
            return createColliderHullGeometry(response.vertices, response.matrices);
        case CollisionMeshShapeType.HEIGHTFIELD:
            throw new Error(`[selection] Shape height field not yet implemented`);
        case CollisionMeshShapeType.HACD:
            throw new Error(`[selection] Shape HACD not yet implemented`);
        case CollisionMeshShapeType.VHACD:
            throw new Error(`[selection] Shape VHACD not yet implemented`);
    }
});
/**
 * Create compound collider geometry based on the compound shape. A custom `createColliderGeometryFn` function can be
 * specified needed otherwise `createColliderGeometry` is used (this helper function is only used by CollisionShape
 * to create BVHBufferGeometry instead of normal BufferGeometry)
 */
export const createColliderCompoundGeometry = (shape_1, connection_1, ...args_1) => __awaiter(void 0, [shape_1, connection_1, ...args_1], void 0, function* (shape, connection, createColliderGeometryFn = createColliderGeometry) {
    const geometries = [];
    for (const compound of shape) {
        // Generate geometry recursively
        const compoundShape = compound.shape;
        const compoundGeometry = yield createColliderGeometryFn(compoundShape, connection);
        if (compoundGeometry === undefined)
            continue;
        // Actually transform the geometry (not the mesh)
        const compoundTransform = compound.trans;
        const transform = new Matrix4();
        transform.compose(new Vector3(...compoundTransform.pos), new Quaternion(...compoundTransform.ori), new Vector3(...compoundTransform.scale));
        compoundGeometry.applyMatrix4(transform);
        geometries.push(compoundGeometry);
    }
    if (geometries.length === 0) {
        // A compound shape can legitimately have no resolvable elements yet (or anymore) - e.g. CompoundColliderManager
        // derives its element list live from descendant CollisionFacets, but other modules (like the renderer, via
        // SelectionHighlightManager) only ever see the plain authored CollisionFacet.shape, which starts out - and can
        // momentarily still be - an empty placeholder array. Treating that as "no geometry available" (matching how
        // CompoundColliderManager's own caller already treats an empty element list) rather than throwing lets callers
        // like the selection highlight fall back to no highlight instead of crashing with an unhandled rejection.
        return undefined;
    }
    // We are merging all geometries into a single geometry. The origin of this
    // merged object might have to be re-centered
    const mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries);
    // Dispose intermediate geometry
    for (const geometry of geometries) {
        geometry.dispose();
    }
    return mergedGeometry;
});
/// Private geometry helper functions ///
const createColliderMeshGeometry = (vertices, indices, matrices) => {
    var _a;
    const geometries = [];
    for (let i = 0; i < vertices.length; i++) {
        const vertexData = new Float32Array(vertices[i]);
        const sourceIndexData = indices[i];
        const geometry = new BufferGeometry();
        geometry.setAttribute('position', new BufferAttribute(vertexData, 3));
        if (sourceIndexData.length > 0) {
            let maxIndex = 0;
            for (let j = 0; j < sourceIndexData.length; j++) {
                maxIndex = Math.max(maxIndex, (_a = sourceIndexData[j]) !== null && _a !== void 0 ? _a : 0);
            }
            const indexData = maxIndex > 65535
                ? new Uint32Array(sourceIndexData)
                : new Uint16Array(sourceIndexData);
            geometry.setIndex(new BufferAttribute(indexData, 1));
        }
        const matrixData = matrices[i];
        const matrix = new Matrix4();
        matrix.fromArray(matrixData);
        geometry.applyMatrix4(matrix);
        geometries.push(geometry);
    }
    if (geometries.length === 0)
        throw new Error(`[selection] Compound geometry list is empty`);
    // We are merging all geometries into a single geometry. The origin of this
    // merged object might have to be re-centered
    const mergedGeometry = BufferGeometryUtils.mergeGeometries(geometries);
    // Dispose intermediate geometry
    for (const geometry of geometries) {
        geometry.dispose();
    }
    return mergedGeometry;
};
const createColliderHullGeometry = (vertices, matrices) => {
    const pointsSize = vertices
        .reduce((acc, geometryVertices) => acc + geometryVertices.length / 3, 0);
    const points = new Array(pointsSize);
    let pointsOffset = 0;
    for (let objectIdx = 0; objectIdx < vertices.length; objectIdx++) {
        const vertexData = vertices[objectIdx];
        const matrixData = matrices[objectIdx];
        const matrix = new Matrix4();
        matrix.fromArray(matrixData);
        // Apply matrix to all vertices and save transformed vertex in points array
        const numberOfVertices = vertices[objectIdx].length / 3;
        for (let vertexIdx = 0; vertexIdx < numberOfVertices; vertexIdx++) {
            const vertexOffset = vertexIdx * 3;
            const point = new Vector3(vertexData[vertexOffset], vertexData[vertexOffset + 1], vertexData[vertexOffset + 2]);
            point.applyMatrix4(matrix);
            points[pointsOffset] = point;
            pointsOffset++;
        }
    }
    return new ConvexGeometry(points);
};
